import { useMemo, useState } from "react";
import { Check, Filter, X } from "lucide-react";
import { cn } from "@/lib/utils";

export interface SearchableOption {
    v: string;
    l: string;
}

interface SearchableListProps {
    options: SearchableOption[];
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
}

// Lista buscável (input + scroll + cap 200 resultados).
// Padrão do prototype: 1 item selecionável (não multi).
export function SearchableList({
    options,
    value,
    onChange,
    placeholder,
}: SearchableListProps) {
    const [query, setQuery] = useState("");
    const filtered = useMemo(() => {
        if (!query.trim()) return options;
        const q = query.toLowerCase();
        return options.filter(
            (o) =>
                String(o.l).toLowerCase().includes(q) ||
                String(o.v).toLowerCase().includes(q)
        );
    }, [query, options]);

    const selectedOpt = options.find((o) => o.v === value);

    return (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="relative border-b border-gray-100">
                <Filter className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={placeholder}
                    className="w-full text-xs pl-8 pr-7 py-2 bg-transparent focus:outline-none placeholder-gray-400"
                />
                {query && (
                    <button
                        onClick={() => setQuery("")}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                    >
                        <X className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>

            <div className="max-h-56 overflow-y-auto">
                {filtered.length === 0 && (
                    <div className="text-center py-6 text-xs text-gray-400">
                        Nenhum resultado para "{query}"
                    </div>
                )}
                {filtered.slice(0, 200).map((o) => {
                    const sel = o.v === value;
                    return (
                        <button
                            key={o.v}
                            onClick={() => onChange(o.v)}
                            className={cn(
                                "w-full flex items-center gap-2 text-left px-2.5 py-1.5 text-xs border-b border-gray-50 last:border-0 transition-colors",
                                sel
                                    ? "bg-blue-50 text-blue-700 font-medium"
                                    : "text-gray-700 hover:bg-gray-50"
                            )}
                        >
                            <span
                                className={cn(
                                    "w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0",
                                    sel
                                        ? "bg-blue-600 border-blue-600"
                                        : "border-gray-300 bg-white"
                                )}
                            >
                                {sel && (
                                    <Check className="w-2.5 h-2.5 text-white" />
                                )}
                            </span>
                            <span className="truncate flex-1">{o.l}</span>
                            {String(o.v).length > 0 && o.v !== "all" && (
                                <span className="text-[10px] font-mono text-gray-400 shrink-0">
                                    #{String(o.v).slice(0, 8)}
                                </span>
                            )}
                        </button>
                    );
                })}
                {filtered.length > 200 && (
                    <div className="text-[11px] text-center text-gray-400 py-2 border-t border-gray-100 bg-gray-50">
                        +{filtered.length - 200} resultados · refine a busca
                    </div>
                )}
            </div>

            <div className="bg-gray-50 border-t border-gray-100 px-2.5 py-1.5 flex items-center justify-between text-[10px] text-gray-500">
                <span>
                    {filtered.length} de {options.length}
                </span>
                {selectedOpt && (
                    <span className="font-medium text-blue-700 truncate max-w-[60%]">
                        ✓ {selectedOpt.l}
                    </span>
                )}
            </div>
        </div>
    );
}
